import { START_LOCATION } from 'vue-router'

import { useCoreStore } from '@/core/store/useCoreStore'

import { type HermesGuardFn } from './types'

/**
 * Ensures the `locationId` query param is present in brand route URLs whenever
 * an active location is set in the store. This keeps the URL accurate so that:
 * - Single-location brands always surface their location ID in the URL
 * - Multi-location brands with a selected location don't lose context when
 *   navigating via programmatic router.push calls that omit query params
 * - A page refresh restores the active location via ContextSwitcher.onMounted,
 *   which reads `locationId` from the URL to rehydrate the store
 *
 * Only applies to brand-scoped routes (those with a `brandId` route param).
 * Only injects locationId when navigating within the currently-loaded brand —
 * skipped during brand switches so the previous brand's locationId is never
 * carried into an unrelated brand's URL.
 * Runs after auth guards so the store is in a valid state.
 *
 * `replace` is used ONLY on the initial navigation (page load / refresh), where
 * we normalize the current URL in place — pushing there would leave the
 * locationId-less URL as a back target and trap the back button. On every
 * subsequent in-app navigation the redirect must preserve push semantics, or it
 * overwrites the previous page's history entry and the browser Back button skips
 * it (e.g. list → detail would clobber the list). See guards/router wiring.
 * @param to
 * @param from
 */
export const ensureLocationIdParam: HermesGuardFn = async (to, from) => {
  if (!to.params.brandId) return undefined

  const { activeLocationId, activeBrandId } = useCoreStore()

  // During a brand switch the store still holds the previous brand's context.
  // Let the navigation proceed unmodified; BrandView will sync the URL after
  // the new brand loads.
  if (to.params.brandId !== activeBrandId) return undefined

  if (activeLocationId && to.query.locationId !== activeLocationId) {
    const isInitialNavigation = !from || from === START_LOCATION
    return {
      hash: to.hash,
      name: to.name,
      params: to.params,
      query: { ...to.query, locationId: activeLocationId },
      replace: isInitialNavigation,
    }
  }

  return undefined
}
